// Plan-EffectiveLimitsAndMaintenanceBanners.md §2b (2026-05-15) +
// Phase D cross-channel awareness (2026-05-15) — per-tab maintenance
// card rendered inside the widget when a channel (chat / voice / both)
// has exhausted its budget AND auto-overage is off for that resource.
//
// Surfaces:
//   - Widget.tsx chat tab when `config.chat_available === false`
//   - Widget.tsx voice tab (replaces VoicePlayground) when
//     `config.voice_available === false`
//
// Channels are INDEPENDENT — chat tab can be in maintenance while voice
// tab is fully functional, and vice versa. The card adapts its copy to
// the cross-channel state:
//   - `channel='chat'` + `otherChannelAvailable=true` → "Try Voice tab"
//   - `channel='voice'` + `otherChannelAvailable=true` → "Try Chat tab"
//   - `channel='all'` → "widget under maintenance" (both down)
//
// The card replaces the channel-specific content (message stream /
// voice connect button) but leaves the widget header + branding
// untouched.
//
// Responsive: copy + mailto link + clickable tab name wrap at 375px
// mobile width. Dark mode uses neutral gray-800 bg with white text +
// brand-accented links.

import React from 'react';
import { Wrench } from 'lucide-react';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '../../config/support';

export type MaintenanceChannel = 'chat' | 'voice' | 'all';

interface Props {
  companyName: string;
  /** Which channel is in maintenance — drives copy. `'all'` means BOTH
   *  chat and voice are exhausted, no cross-channel suggestion. */
  channel: MaintenanceChannel;
  /** True when the OTHER channel (not `channel`) is still functional.
   *  Ignored when `channel='all'`. When true, the card renders a
   *  clickable inline reference to the other tab. */
  otherChannelAvailable?: boolean;
  /** Click handler for the inline tab-switch suggestion. Ignored when
   *  `channel='all'` OR `otherChannelAvailable` is false. */
  onSwitchToOther?: () => void;
  /** Theme hint from the parent widget (matches Widget.tsx `theme` /
   *  VoicePlayground `theme` prop). Defaults to light if absent. */
  isDark?: boolean;
  /** Optional brand color from the widget config — used for the mailto
   *  link + clickable tab-name accent. Defaults to a neutral primary
   *  blue. */
  brandColor?: string;
}

const MaintenanceCard: React.FC<Props> = ({
  companyName,
  channel,
  otherChannelAvailable = false,
  onSwitchToOther,
  isDark = false,
  brandColor,
}) => {
  const accent = brandColor || (isDark ? '#60a5fa' : '#2563eb');
  const company = companyName || 'this workspace';

  // Other-tab label for the inline cross-channel suggestion. Capitalized
  // to read like a proper noun (the user clicks it as a button — same
  // typographic weight as a tab name).
  const otherLabel = channel === 'chat' ? 'Voice' : channel === 'voice' ? 'Chat' : '';
  const showSwitchSuggestion =
    channel !== 'all' && otherChannelAvailable && !!onSwitchToOther;

  return (
    <div
      className={`flex-1 flex items-center justify-center p-4 sm:p-6 ${
        isDark ? 'bg-black' : 'bg-gray-50'
      }`}
    >
      <div
        className={`w-full max-w-sm rounded-2xl shadow-sm border p-5 sm:p-6 text-center ${
          isDark
            ? 'bg-gray-900 border-gray-800 text-gray-100'
            : 'bg-white border-gray-200 text-gray-800'
        }`}
        role="status"
        aria-live="polite"
      >
        <div
          className={`mx-auto mb-3 w-12 h-12 rounded-full flex items-center justify-center ${
            isDark ? 'bg-gray-800' : 'bg-gray-100'
          }`}
        >
          <Wrench
            className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
            aria-hidden="true"
          />
        </div>

        {/* Headline. Channel-specific when one side is up, generic
            "widget under maintenance" when both are down.
            Plan-TextVoicePoolBilling-2026-08 §3 — the chat variant names the
            cause, because on the pooled Text allowance the only thing that
            reaches this card is an exhausted allowance, and "temporarily
            unavailable" reads like an outage the visitor should wait out.
            🚨 Visitor-facing surface: the reader is the merchant's customer,
            not the account owner. No plan / billing / token / quota wording,
            and never an instruction to go upgrade something. */}
        {channel === 'all' ? (
          <p className="text-sm leading-relaxed">
            The widget for <span className="font-semibold">{company}</span> is
            currently under maintenance.
          </p>
        ) : channel === 'chat' ? (
          <p className="text-sm leading-relaxed">
            <span className="font-semibold">Chat</span> is unavailable for{' '}
            <span className="font-semibold">{company}</span> right now — the AI
            assistant has used up its message allowance for this period.
          </p>
        ) : (
          <p className="text-sm leading-relaxed">
            <span className="font-semibold capitalize">{channel}</span> is
            temporarily unavailable for{' '}
            <span className="font-semibold">{company}</span>.
          </p>
        )}

        {/* Body — cross-channel suggestion (when applicable) + always-
            present mailto link. Renders as a single paragraph so the
            two clickable elements share a single visual flow on mobile. */}
        <p
          className={`text-sm mt-2 leading-relaxed ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}
        >
          {showSwitchSuggestion ? (
            <>
              Try the{' '}
              <button
                type="button"
                onClick={onSwitchToOther}
                className="font-semibold underline hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-1 rounded-sm"
                style={{
                  color: accent,
                  textDecorationStyle: 'dotted',
                }}
                aria-label={`Switch to ${otherLabel} tab`}
              >
                {otherLabel}
              </button>{' '}
              tab instead, or reach out to{' '}
              <a
                href={SUPPORT_MAILTO}
                className="font-semibold underline break-all hover:opacity-80"
                style={{ color: accent }}
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </>
          ) : (
            <>
              Reach out to{' '}
              <a
                href={SUPPORT_MAILTO}
                className="font-semibold underline break-all hover:opacity-80"
                style={{ color: accent }}
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              instead.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default MaintenanceCard;
